/**
 * TrouveChatPanel.jsx — the live conversational chat surface.
 *
 * Used in two places:
 *   • As a slide-up overlay from the persistent "Ask Trouvé" floating
 *     button in the AR mirror flow (LiveCoach.jsx)
 *   • As the body of the dedicated `/trouve/chat` page
 *
 * Drives:
 *   • Multi-turn chat with GPT-5.2 via /trouve/chat/sessions/{id}/message
 *   • Voice input via MediaRecorder → /trouve/voice/transcribe (Whisper)
 *   • Image attach via webcam snap or file picker (sent as image_base64)
 *
 * Gating UI states:
 *   • upgrade_required → "This is a Pro perk" upgrade prompt + secondary CTA
 *   • quota_exceeded   → "You've used today's questions" upgrade prompt
 *   • allowed (referred_quota) → renders quota_left line above input
 *   • allowed (pro/ambassador) → no quota line
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Camera, X, Send, Loader2, Sparkles, ChevronDown, Volume2, VolumeX, Download, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  fetchTrouveAccess,
  createTrouveSession,
  sendTrouveMessage,
  finalizeTrouveSession,
  transcribeTrouveVoice,
  speakTrouveText,
  downloadTrouveAudio,
} from "../lib/api";
import { track } from "../lib/analytics";
import TrouveRecommendationChip from "./TrouveRecommendationChip";

const MAX_IMAGE_BYTES = 4 * 1024 * 1024; // 4 MB before base64 inflate
const VOICE_PREF_KEY = "trubeaute.trouveVoice.enabled";

const _voicePrefInitial = () => {
  try {
    const v = window.localStorage.getItem(VOICE_PREF_KEY);
    // Default ON — the whole point is hands-busy voice coaching.
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
};

const TrouveChatPanel = ({ onClose, autoStart = true, dataTestidPrefix = "trouve-chat" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [access, setAccess] = useState(null);          // {allowed, reason, quota_left, daily_limit}
  const [accessLoading, setAccessLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);        // {role, text, has_image, created_at}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [imageB64, setImageB64] = useState(null);

  // Voice recording state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const messagesScrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── TTS playback state ────────────────────────────────────────
  const [voiceEnabled, setVoiceEnabled] = useState(_voicePrefInitial);
  const [playingIdx, setPlayingIdx] = useState(null);    // index of msg currently playing
  const audioRef = useRef(null);
  const lastPlayedAssistantIdxRef = useRef(-1);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      try {
        if (audioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
      } catch { /* noop */ }
      audioRef.current = null;
    }
    setPlayingIdx(null);
  }, []);

  const playAssistantMessage = useCallback(async (idx, text) => {
    if (!text) return;
    stopPlayback();
    setPlayingIdx(idx);
    try {
      const url = await speakTrouveText(text);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingIdx((cur) => (cur === idx ? null : cur));
      };
      audio.onerror = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingIdx((cur) => (cur === idx ? null : cur));
      };
      await audio.play();
      track("trouve_chat_tts_played", { auto: idx === lastPlayedAssistantIdxRef.current });
    } catch (err) {
      // Autoplay blocked (browsers require a user gesture for first
      // playback) or TTS fetch failed — silent fallback. User can
      // re-tap the message to play manually.
      setPlayingIdx(null);
    }
  }, [stopPlayback]);

  // Auto-play newest assistant message when voice is enabled.
  useEffect(() => {
    if (!voiceEnabled || messages.length === 0) return;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    if (last?.role !== "assistant") return;
    if (lastIdx === lastPlayedAssistantIdxRef.current) return;
    lastPlayedAssistantIdxRef.current = lastIdx;
    playAssistantMessage(lastIdx, last.text);
  }, [messages, voiceEnabled, playAssistantMessage]);

  // Cleanup audio on unmount
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(VOICE_PREF_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      if (!next) stopPlayback();
      track("trouve_chat_voice_toggled", { enabled: next });
      return next;
    });
  }, [stopPlayback]);

  const handleDownloadAudio = useCallback(async (msg) => {
    if (!msg?.text) return;
    try {
      const filename = await downloadTrouveAudio(msg.text, { hint: msg.text });
      track("trouve_chat_audio_downloaded", { chars: msg.text.length });
      toast.success(filename);
    } catch {
      toast.error(t("trouveChat.errorGeneric"));
    }
  }, [t]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
    }
  }, [messages, sending, transcribing]);

  // Resolve access + auto-start session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await fetchTrouveAccess();
        if (cancelled) return;
        setAccess(a);
        if (a?.allowed && autoStart) {
          const s = await createTrouveSession();
          if (cancelled) return;
          setSessionId(s.session_id);
          setAccess(s.quota);
          track("trouve_chat_opened", { reason: a.reason, quota_left: a.quota_left });
        }
      } catch (err) {
        if (cancelled) return;
        // 402/429 land here with detail.error set — surface gating directly
        const d = err?.response?.data?.detail;
        if (d?.reason) {
          setAccess({ allowed: false, reason: d.reason, quota_left: d.quota_left, daily_limit: d.daily_limit });
        } else {
          setAccess({ allowed: false, reason: "upgrade_required" });
        }
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [autoStart]);

  // Cleanup mic stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
      }
    };
  }, []);

  const handleImagePicked = useCallback((file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t("trouveChat.imageTooLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") {
        setImageB64(result); // includes data: prefix; backend strips
        track("trouve_chat_image_attached", { source: "picker" });
      }
    };
    reader.readAsDataURL(file);
  }, [t]);

  const startRecording = useCallback(async () => {
    if (recording) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error(t("trouveChat.voiceUnsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Prefer webm/opus — universally supported by Whisper.
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType || "audio/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((tr) => tr.stop());
          streamRef.current = null;
        }
        if (blob.size < 256) {
          // user tapped mic accidentally — silent abort
          return;
        }
        setTranscribing(true);
        try {
          const namedBlob = new File([blob], "voice.webm", { type: blob.type });
          const { text } = await transcribeTrouveVoice(namedBlob);
          if (text) {
            setInput((prev) => (prev ? prev + " " : "") + text);
            track("trouve_chat_voice_transcribed", { chars: text.length });
          }
        } catch (err) {
          const reason = err?.response?.data?.detail;
          if (typeof reason === "string" && reason === "no_speech_detected") {
            // silent — user didn't say anything
          } else {
            toast.error(t("trouveChat.errorGeneric"));
          }
        } finally {
          setTranscribing(false);
        }
      };
      recorder.start();
      setRecording(true);
      track("trouve_chat_voice_record_start");
    } catch (err) {
      toast.error(t("trouveChat.micPermissionDenied"));
    }
  }, [recording, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  const handleSend = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || !sessionId || sending) return;
    setSending(true);
    const payload = { text, image_base64: imageB64 || undefined };
    // Optimistic append
    setMessages((prev) => [
      ...prev,
      { role: "user", text, has_image: !!imageB64, created_at: new Date().toISOString() },
    ]);
    setInput("");
    setImageB64(null);
    try {
      const res = await sendTrouveMessage(sessionId, payload);
      setMessages((prev) => [
        ...prev,
        { ...res.assistant_message },
      ]);
      if (res.quota) setAccess(res.quota);
      track("trouve_chat_message_sent", {
        had_image: !!payload.image_base64,
        chars: text.length,
      });
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      // Roll back optimistic user message on hard fail
      setMessages((prev) => prev.slice(0, -1));
      if (status === 429) {
        setAccess({ allowed: false, reason: "quota_exceeded", quota_left: 0 });
        toast.error(t("trouveChat.errorQuota"));
      } else if (status === 402) {
        setAccess({ allowed: false, reason: "upgrade_required" });
        toast.error(t("trouveChat.errorUpgrade"));
      } else {
        toast.error(t("trouveChat.errorGeneric"));
      }
    } finally {
      setSending(false);
    }
  }, [input, sessionId, sending, imageB64, t]);

  const handleEndChat = useCallback(async () => {
    if (!sessionId || messages.length === 0) {
      if (onClose) onClose();
      return;
    }
    if (!window.confirm(t("trouveChat.endChatConfirmBody"))) return;
    try {
      await finalizeTrouveSession(sessionId);
      track("trouve_chat_finalized", { messages: messages.length });
      toast.success(t("trouveChat.endChatConfirmCta"));
    } catch {
      // not fatal — just close anyway
    }
    if (onClose) onClose();
    else navigate("/my-trouve-chats");
  }, [sessionId, messages.length, onClose, t, navigate]);

  // ── Render gating states ─────────────────────────────────────
  if (accessLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[400px]"
        data-testid={`${dataTestidPrefix}-loading`}
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
      </div>
    );
  }

  if (!access?.allowed) {
    const reason = access?.reason || "upgrade_required";
    const titleKey = reason === "quota_exceeded"
      ? "trouveChat.quotaExceededTitle"
      : "trouveChat.upgradeRequiredTitle";
    const bodyKey = reason === "quota_exceeded"
      ? "trouveChat.quotaExceededBody"
      : "trouveChat.upgradeRequiredBody";
    const ctaKey = reason === "quota_exceeded"
      ? "trouveChat.quotaExceededCta"
      : "trouveChat.upgradeRequiredCta";
    return (
      <div
        className="p-8 max-w-md mx-auto text-center"
        data-testid={`${dataTestidPrefix}-blocked-${reason}`}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#FBE7EE] to-[#9B6A6C] flex items-center justify-center mb-5">
          <Crown className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-2xl font-serif italic text-[#2C2123] mb-3">
          {t(titleKey)}
        </h2>
        <p className="text-[#5C4B51] text-sm leading-relaxed mb-6">
          {t(bodyKey)}
        </p>
        <button
          type="button"
          onClick={() => navigate("/pricing")}
          className="inline-block bg-[#9B6A6C] text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-[#8a5a5d] transition-colors"
          data-testid={`${dataTestidPrefix}-upgrade-btn`}
        >
          {t(ctaKey)}
        </button>
        {reason === "upgrade_required" && (
          <p className="mt-4 text-xs text-[#8A7D82]">
            {t("trouveChat.upgradeRequiredSecondary")}
          </p>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="mt-6 block mx-auto text-sm text-[#8A7D82] hover:text-[#5C4B51]"
            data-testid={`${dataTestidPrefix}-close-btn`}
          >
            {t("common.close")}
          </button>
        )}
      </div>
    );
  }

  // ── Allowed: chat UI ───────────────────────────────────────────
  const quotaLeft = access?.quota_left;
  const showQuotaLine = access?.reason === "referred_quota" && typeof quotaLeft === "number";

  return (
    <div
      className="flex flex-col h-full bg-[#FDFBF7]"
      data-testid={`${dataTestidPrefix}-panel`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#EADDD7] bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#FBE7EE] to-[#9B6A6C] flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[#2C2123] text-sm">{t("trouveChat.headerTitle")}</div>
            <div className="text-xs text-[#8A7D82] truncate">{t("trouveChat.headerSubtitle")}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={toggleVoice}
            aria-label={voiceEnabled ? t("trouveChat.voiceMute") : t("trouveChat.voiceUnmute")}
            className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
              voiceEnabled
                ? "bg-[#FBE7EE] text-[#9B6A6C] hover:bg-[#f5d8e0]"
                : "bg-[#FDFBF7] text-[#8A7D82] hover:bg-[#FBE7EE]"
            }`}
            data-testid={`${dataTestidPrefix}-voice-toggle`}
          >
            {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleEndChat}
              className="text-xs text-[#8A7D82] hover:text-[#9B6A6C] px-2 py-1 rounded-full hover:bg-[#FDFBF7] transition-colors"
              data-testid={`${dataTestidPrefix}-end-btn`}
            >
              {t("trouveChat.endChat")}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="h-8 w-8 rounded-full hover:bg-[#FDFBF7] flex items-center justify-center text-[#5C4B51]"
              data-testid={`${dataTestidPrefix}-close-btn`}
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesScrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
        data-testid={`${dataTestidPrefix}-messages`}
      >
        {messages.length === 0 && (
          <div className="text-center max-w-sm mx-auto py-8" data-testid={`${dataTestidPrefix}-empty`}>
            <h3 className="text-xl font-serif italic text-[#2C2123] mb-2">
              {t("trouveChat.emptyStateTitle")}
            </h3>
            <p className="text-sm text-[#5C4B51] mb-5">
              {t("trouveChat.emptyStateBody")}
            </p>
            <div className="flex flex-col gap-2 mt-4">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleSend(t(`trouveChat.emptyStateSuggestion${n}`))}
                  className="text-left text-xs text-[#9B6A6C] bg-white border border-[#EADDD7] rounded-full px-4 py-2 hover:bg-[#FBE7EE] transition-colors"
                  data-testid={`${dataTestidPrefix}-suggestion-${n}`}
                >
                  {t(`trouveChat.emptyStateSuggestion${n}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m, idx) => {
            const isAssistant = m.role === "assistant";
            const isPlaying = playingIdx === idx;
            return (
              <motion.div
                key={`${m.created_at}-${idx}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`${dataTestidPrefix}-msg-${m.role}-${idx}`}
              >
                {/* Trouvé avatar next to each assistant bubble — small
                    visual anchor that reinforces "Trouvé is the one
                    speaking" instead of an anonymous chatbot. Only
                    rendered on the first message of an assistant
                    streak so the column reads cleanly. */}
                {isAssistant && (
                  <div
                    className="h-7 w-7 rounded-full overflow-hidden ring-1 ring-[#D4B483]/40 shrink-0 mb-1"
                    aria-hidden="true"
                    style={{
                      visibility:
                        idx === 0 || messages[idx - 1]?.role !== "assistant"
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    <img
                      src="/trouve/conversation-avatar.png"
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                )}
                <div className={`flex flex-col gap-1 max-w-[82%] ${isAssistant ? "items-start" : "items-end"}`}>
                  <div
                    onClick={isAssistant ? () => (isPlaying ? stopPlayback() : playAssistantMessage(idx, m.text)) : undefined}
                    className={`group px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap relative ${
                      m.role === "user"
                        ? "bg-[#9B6A6C] text-white rounded-br-sm"
                        : "bg-white border border-[#EADDD7] text-[#2C2123] rounded-bl-sm cursor-pointer hover:border-[#9B6A6C]/40"
                    } ${isPlaying ? "ring-2 ring-[#9B6A6C]/30" : ""}`}
                  >
                    {m.has_image && m.role === "user" && (
                      <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">
                        📷 {t("trouveChat.imageAttached")}
                      </div>
                    )}
                    {m.text}
                    {isAssistant && isPlaying && (
                      <span
                        className="inline-flex ml-2 items-center gap-0.5 align-middle text-[#9B6A6C]"
                        aria-label={t("trouveChat.speaking")}
                        data-testid={`${dataTestidPrefix}-playing-${idx}`}
                      >
                        <span className="h-2 w-0.5 bg-current rounded animate-pulse" style={{ animationDelay: "0ms" }} />
                        <span className="h-3 w-0.5 bg-current rounded animate-pulse" style={{ animationDelay: "120ms" }} />
                        <span className="h-2 w-0.5 bg-current rounded animate-pulse" style={{ animationDelay: "240ms" }} />
                      </span>
                    )}
                  </div>
                  {isAssistant && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDownloadAudio(m); }}
                      aria-label={t("trouveChat.saveAudio")}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8A7D82] hover:text-[#9B6A6C] transition-colors px-1.5 py-1"
                      data-testid={`${dataTestidPrefix}-download-${idx}`}
                    >
                      <Download className="h-3 w-3" />
                      {t("trouveChat.saveAudio")}
                    </button>
                  )}
                  {isAssistant && Array.isArray(m.recommendations) && m.recommendations.length > 0 && (
                    <div
                      className="flex flex-wrap gap-1.5 mt-1"
                      data-testid={`${dataTestidPrefix}-recommendations-${idx}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {m.recommendations.map((rec, ri) => (
                        <TrouveRecommendationChip
                          key={`${m.message_id || idx}-rec-${ri}`}
                          recommendation={rec}
                          messageId={m.message_id}
                          sessionId={sessionId}
                          dataTestidPrefix={`${dataTestidPrefix}-rec`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {sending && (
          <div className="flex justify-start" data-testid={`${dataTestidPrefix}-thinking`}>
            <div className="bg-white border border-[#EADDD7] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-[#8A7D82] italic flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("trouveChat.thinking")}
            </div>
          </div>
        )}
      </div>

      {/* Quota line */}
      {showQuotaLine && (
        <div
          className="px-4 py-1.5 text-xs text-center text-[#9B6A6C] bg-[#FBE7EE] border-t border-[#EADDD7]"
          data-testid={`${dataTestidPrefix}-quota-line`}
        >
          {t("trouveChat.quotaLeftLine", { count: quotaLeft })}
        </div>
      )}

      {/* Image preview chip */}
      {imageB64 && (
        <div className="px-4 py-2 border-t border-[#EADDD7] bg-white flex items-center gap-2">
          <img src={imageB64} alt={t("trouveChat.imageAttached")} className="h-12 w-12 rounded-lg object-cover border border-[#EADDD7]" />
          <span className="text-xs text-[#5C4B51] flex-1">{t("trouveChat.imageAttached")}</span>
          <button
            type="button"
            onClick={() => setImageB64(null)}
            aria-label={t("trouveChat.removeImage")}
            className="h-7 w-7 rounded-full hover:bg-[#FDFBF7] flex items-center justify-center text-[#5C4B51]"
            data-testid={`${dataTestidPrefix}-remove-image`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Input row */}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
        className="flex items-center gap-2 px-3 py-3 border-t border-[#EADDD7] bg-white"
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          aria-label={t("trouveChat.showTrouve")}
          className="h-10 w-10 rounded-full bg-[#FDFBF7] hover:bg-[#FBE7EE] flex items-center justify-center text-[#9B6A6C] transition-colors shrink-0"
          data-testid={`${dataTestidPrefix}-image-btn`}
        >
          <Camera className="h-5 w-5" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleImagePicked(e.target.files?.[0])}
          data-testid={`${dataTestidPrefix}-image-input`}
        />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={transcribing ? t("trouveChat.transcribing") : t("trouveChat.placeholderText")}
          disabled={sending || transcribing || recording}
          className="flex-1 bg-[#FDFBF7] border border-[#EADDD7] rounded-full px-4 py-2 text-sm placeholder:text-[#8A7D82] focus:outline-none focus:border-[#9B6A6C] disabled:opacity-60"
          data-testid={`${dataTestidPrefix}-text-input`}
        />
        {input.trim() ? (
          <button
            type="submit"
            disabled={sending}
            aria-label={t("trouveChat.send")}
            className="h-10 w-10 rounded-full bg-[#9B6A6C] hover:bg-[#8a5a5d] disabled:opacity-60 flex items-center justify-center text-white transition-colors shrink-0"
            data-testid={`${dataTestidPrefix}-send-btn`}
          >
            <Send className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={sending || transcribing}
            aria-label={recording ? t("trouveChat.stop") : t("trouveChat.recording")}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${
              recording
                ? "bg-[#9B6A6C] text-white animate-pulse"
                : "bg-[#FDFBF7] hover:bg-[#FBE7EE] text-[#9B6A6C]"
            }`}
            data-testid={`${dataTestidPrefix}-mic-btn`}
          >
            {recording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>
        )}
      </form>
    </div>
  );
};

export default TrouveChatPanel;
